
import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { SharedFloorPlanCard } from "./SharedFloorPlanCard";
import { Card } from "@/components/ui/card";
import { FloorPlanFilters } from "../dashboard/FloorPlanFilters";

interface SharedFloorPlan {
  id: string;
  name: string;
  preview_image: string | null;
  updated_at: string;
  content: any;
  user_id: string;
  user: {
    id: string;
    display_name: string | null;
    username: string | null;
    avatar_url: string | null;
  } | null;
  average_rating: number | null;
  ratings_count: number | null;
}

export function SharedFloorPlansList() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");

  const { data: sharedPlans, isLoading, error } = useQuery({
    queryKey: ['shared-floor-plans'],
    queryFn: async () => {
      console.log("Fetching shared floor plans...");
      const { data, error } = await supabase
        .from('floor_plans')
        .select(`
          *,
          user:profiles(
            id,
            display_name,
            username,
            avatar_url
          ),
          average_rating:floor_plan_ratings(rating),
          ratings_count:floor_plan_ratings(count)
        `)
        .eq('is_public', true)
        .order('updated_at', { ascending: false });

      if (error) {
        console.error("Error fetching shared floor plans:", error);
        throw error;
      }

      console.log("Received data:", data);
      
      // Calculate average rating and count for each plan
      const plansWithMetrics = data.map(plan => {
        const ratings = plan.average_rating || [];
        const avgRating = ratings.length > 0 
          ? ratings.reduce((sum, r) => sum + r.rating, 0) / ratings.length 
          : 0;
        
        return {
          ...plan,
          average_rating: avgRating,
          ratings_count: ratings.length
        };
      });
      
      // Sort by average rating (most popular first)
      plansWithMetrics.sort((a, b) => (b.average_rating || 0) - (a.average_rating || 0));
      
      return plansWithMetrics as SharedFloorPlan[];
    }
  });

  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('name', { ascending: true });

      if (error) {
        console.error("Error fetching categories:", error);
        throw error;
      }

      return data;
    }
  });

  const filteredPlans = React.useMemo(() => {
    if (!sharedPlans) return [];

    return sharedPlans.filter((plan) => {
      const matchesSearch = plan.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = !selectedCategory || selectedCategory === "all";
      
      return matchesSearch && matchesCategory;
    });
  }, [sharedPlans, searchQuery, selectedCategory]);

  if (error) {
    console.error("Error in SharedFloorPlansList:", error);
    return (
      <Card className="p-8 text-center">
        <p className="text-muted-foreground">
          Er is een fout opgetreden bij het ophalen van de plattegronden.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <FloorPlanFilters
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        selectedFolder={selectedCategory}
        setSelectedFolder={setSelectedCategory}
        folders={categories?.map(cat => ({ id: cat.id, name: cat.name })) || []}
      />
      
      {isLoading ? (
        <div className="text-center py-8 text-muted-foreground">
          Laden...
        </div>
      ) : !filteredPlans.length ? (
        <Card className="p-8 text-center">
          <p className="text-muted-foreground">
            Er zijn nog geen gedeelde plattegronden beschikbaar.
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPlans.map((plan) => (
            <SharedFloorPlanCard key={plan.id} plan={plan} />
          ))}
        </div>
      )}
    </div>
  );
}
